import { useState, useEffect } from 'react';
import { cx } from '@parrot/utils';
import { getTheme, setTheme, type Theme } from '../../lib/theme';
import styles from './ThemeToggle.module.css';

export interface ThemeToggleProps {
  /** 'icon' = compact morph button, 'switch' = full toggle with track */
  variant?: 'icon' | 'switch';
  /** Optional className */
  className?: string;
}

/**
 * Theme toggle with two variants:
 * - icon: Compact sun/moon morph button for tight spaces
 * - switch: Full toggle with animated track, stars, and clouds
 */
export function ThemeToggle({ variant = 'switch', className }: ThemeToggleProps) {
  const [theme, setThemeState] = useState<Theme>(getTheme);

  // Sync with system changes
  useEffect(() => {
    const handler = () => setThemeState(getTheme());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  function handleToggle() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setThemeState(next);
  }

  const isDark = theme === 'dark';

  if (variant === 'icon') {
    return (
      <button
        type="button"
        className={cx(styles.iconBtn, className)}
        onClick={handleToggle}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        aria-pressed={isDark}
      >
        <span className={styles.iconOrb}>
          {/* Sun rays - visible in light mode */}
          <span className={cx(styles.rays, !isDark && styles.raysVisible)}>
            {[...Array(8)].map((_, i) => (
              <span
                key={i}
                className={styles.ray}
                style={{ '--ray-index': i } as React.CSSProperties}
              />
            ))}
          </span>
          {/* Core circle - silvery moon in dark, gold sun in light (handled by CSS) */}
          <span className={styles.core}>
            {/* Crater creates crescent - visible in dark mode, hidden in light */}
            <span className={cx(styles.crater, !isDark && styles.craterHidden)} />
          </span>
        </span>
      </button>
    );
  }

  // Switch variant
  return (
    <button
      type="button"
      className={cx(styles.switch, className)}
      onClick={handleToggle}
      role="switch"
      aria-checked={!isDark}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <span className={cx(styles.track, !isDark && styles.trackLight)}>
        {/* Stars - visible in dark mode */}
        <span className={cx(styles.stars, isDark && styles.starsVisible)}>
          <span
            className={styles.star}
            style={{ '--star-x': '65%', '--star-y': '25%', '--star-size': '3px' } as React.CSSProperties}
          />
          <span
            className={styles.star}
            style={{ '--star-x': '75%', '--star-y': '55%', '--star-size': '2px' } as React.CSSProperties}
          />
          <span
            className={styles.star}
            style={{ '--star-x': '85%', '--star-y': '35%', '--star-size': '2px' } as React.CSSProperties}
          />
          <span
            className={styles.star}
            style={{ '--star-x': '70%', '--star-y': '70%', '--star-size': '2px' } as React.CSSProperties}
          />
        </span>

        {/* Cloud - visible in light mode */}
        <span className={cx(styles.cloud, !isDark && styles.cloudVisible)}>
          <svg viewBox="0 0 32 20" className={styles.cloudSvg}>
            <path
              d="M25 18H7a5 5 0 0 1-.5-9.97A7 7 0 0 1 20 5.3 5 5 0 0 1 25 10a4 4 0 0 1 0 8z"
              fill="currentColor"
            />
          </svg>
        </span>

        {/* Thumb - silvery moon / gold sun */}
        <span className={cx(styles.thumb, !isDark && styles.thumbLight)}>
          {/* Moon crescent shadow - visible in dark, hidden in light */}
          <span className={cx(styles.moonShadow, !isDark && styles.moonShadowHidden)} />
        </span>
      </span>
    </button>
  );
}
